import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FileText, CheckCircle, Clock, AlertTriangle, Users,
  GraduationCap, Calendar, Activity,
  Server, Bell, ChevronRight, UserPlus
} from 'lucide-react';
import { getUser, authenticatedFetch } from '@/lib/auth';
import { formatStatus, getStatusColor } from '@/utils/format';
import type { User } from '@/types';

interface DashboardStats {
  user: {
    id: string;
    role: string;
    full_name: string;
    study_association_name: string | null;
  };
  actions_required: ActionItem[];
  study_plans: {
    total: number;
    draft: number;
    submitted: number;
    under_review: number;
    approved: number;
    rejected: number;
    new_30d: number;
    approved_30d: number;
    avg_score: string | null;
  };
  courses_exams: {
    active_courses: number;
    total_candidates: number;
    pending_grading: number;
    graded_total: number;
    graded_30d: number;
    completed_30d: number;
    pass_rate_30d: number | null;
  };
  sensors: {
    total: number;
    active: number;
    pending_grading: number;
  };
  upcoming_deadlines: Deadline[];
  recent_activity: ActivityItem[];
  system_stats: {
    total_associations: number;
    total_users: number;
    active_users: number;
  } | null;
}

interface ActionItem {
  type: string;
  priority: 'high' | 'medium' | 'low';
  count: number;
  title: string;
  link: string;
}

interface Deadline {
  id: string;
  title: string;
  exam_title: string | null;
  deadline: string;
  candidates: number;
}

interface ActivityItem {
  type: string;
  title: string;
  status: string;
  date: string;
  extra: string | null;
}

export default function Dashboard() {
  const [user, setUser] = useState<User | null>(null);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const currentUser = getUser();
      if (!currentUser) {
        setError('Bruker ikke funnet');
        setLoading(false);
        return;
      }

      setUser(currentUser);

      // Fetch dashboard stats from new API
      const response = await authenticatedFetch('/api/dashboard/stats', {
        method: 'POST',
        body: JSON.stringify({ user_id: currentUser.id }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to load dashboard');
      }

      setStats(result);
    } catch (err: any) {
      console.error('Error loading dashboard:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-kf-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Laster dashboard...</p>
        </div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="mx-auto h-12 w-12 text-red-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Kunne ikke laste dashboard</h2>
        <p className="text-gray-600">{error || 'Ukjent feil'}</p>
        <button
          onClick={loadDashboardData}
          className="mt-4 px-4 py-2 bg-kf-primary text-white rounded-lg hover:bg-kf-secondary"
        >
          Prøv igjen
        </button>
      </div>
    );
  }

  const getRoleLabel = () => {
    switch (user?.role) {
      case 'systemadmin': return 'Systemadministrator';
      case 'admin': return 'Administrator';
      case 'member': return stats.user.study_association_name || 'Kursleverandør';
      case 'sensor': return 'Sensor';
      default: return 'Bruker';
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit' });
  };

  const formatRelativeDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'i dag';
    if (diffDays === 1) return 'i går';
    if (diffDays < 7) return `${diffDays} dager siden`;
    return formatDate(dateStr);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="mb-2">
        <h1 className="text-3xl font-bold text-gray-900">Statusoversikt</h1>
        <p className="text-gray-600">
          {getRoleLabel()} - {stats.user.full_name}
        </p>
      </div>

      {/* Actions Required Section */}
      {stats.actions_required.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Bell className="h-5 w-5 text-amber-500" />
            Krever handling
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {stats.actions_required.map((action, index) => (
              <ActionCard key={index} action={action} />
            ))}
          </div>
        </div>
      )}

      {/* Two-column layout: Study Plans | Courses & Exams */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Study Plans Section - NOT shown for sensors */}
        {user?.role !== 'sensor' && (
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <FileText className="h-5 w-5 text-kf-primary" />
                Studieplaner
              </h2>
              <Link to="/study-plans" className="text-sm text-kf-primary hover:underline flex items-center gap-1">
                Se alle <ChevronRight className="h-4 w-4" />
              </Link>
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-4 gap-2 mb-4">
              <MiniStat label="Totalt" value={stats.study_plans.total} />
              <MiniStat label="Godkjent" value={stats.study_plans.approved} color="green" />
              <MiniStat label="Vurderes" value={stats.study_plans.submitted + stats.study_plans.under_review} color="yellow" />
              <MiniStat label="Avvist" value={stats.study_plans.rejected} color="red" />
            </div>

            {/* 30 days summary */}
            <div className="bg-gray-50 rounded-lg p-3 text-sm">
              <div className="flex items-center gap-2 text-gray-600 mb-1">
                <Activity className="h-4 w-4" />
                <span className="font-medium">Siste 30 dager</span>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-gray-700">
                <span>{stats.study_plans.new_30d} nye</span>
                <span>{stats.study_plans.approved_30d} godkjent</span>
                {stats.study_plans.avg_score && (
                  <span className="text-kf-primary font-medium">Gj.snitt: {stats.study_plans.avg_score}%</span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Sensor Welcome Section - only for sensors with no courses */}
        {user?.role === 'sensor' && stats.courses_exams.active_courses === 0 && (
          <div className="card lg:col-span-2">
            <div className="text-center py-8">
              <GraduationCap className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Velkommen som sensor!</h2>
              <p className="text-gray-600 max-w-md mx-auto">
                Du har ingen kurs tildelt ennå. Når du blir tildelt som sensor for et kurs,
                vil du se prøver som venter på sensur her.
              </p>
              <Link
                to="/sensor/dashboard"
                className="mt-4 inline-flex items-center gap-2 text-kf-primary hover:underline"
              >
                Gå til sensordashboard <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        )}

        {/* Courses & Exams Section - show for sensors with assigned courses, or for other roles with data */}
        {((user?.role === 'sensor' && stats.courses_exams.active_courses > 0) ||
          (user?.role !== 'sensor' && (stats.courses_exams.active_courses > 0 || stats.courses_exams.total_candidates > 0))) && (
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <GraduationCap className="h-5 w-5 text-kf-primary" />
                {user?.role === 'sensor' ? 'Dine tildelte kurs' : 'Kurs og prøver'}
              </h2>
              <Link
                to={user?.role === 'sensor' ? '/sensor/dashboard' : '/courses-and-exams'}
                className="text-sm text-kf-primary hover:underline flex items-center gap-1"
              >
                {user?.role === 'sensor' ? 'Sensordashboard' : 'Se alle'} <ChevronRight className="h-4 w-4" />
              </Link>
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-4 gap-2 mb-4">
              <MiniStat label={user?.role === 'sensor' ? 'Tildelte kurs' : 'Aktive kurs'} value={stats.courses_exams.active_courses} />
              <MiniStat label="Kandidater" value={stats.courses_exams.total_candidates} />
              <MiniStat label="Sensurert" value={stats.courses_exams.graded_total} color="green" />
              <MiniStat label="Til sensur" value={stats.courses_exams.pending_grading} color="yellow" />
            </div>

            {/* 30 days summary - hide for sensors */}
            {user?.role !== 'sensor' && (
              <div className="bg-gray-50 rounded-lg p-3 text-sm">
                <div className="flex items-center gap-2 text-gray-600 mb-1">
                  <Activity className="h-4 w-4" />
                  <span className="font-medium">Siste 30 dager</span>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-gray-700">
                  <span>{stats.courses_exams.completed_30d} fullført</span>
                  <span>{stats.courses_exams.graded_30d} sensurert</span>
                  {stats.courses_exams.pass_rate_30d !== null && (
                    <span className="text-green-600 font-medium">{stats.courses_exams.pass_rate_30d}% bestått</span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Upcoming Deadlines */}
      {stats.upcoming_deadlines.length > 0 && (
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
            <Calendar className="h-5 w-5 text-amber-500" />
            Kommende frister (7 dager)
          </h2>
          <div className="space-y-2">
            {stats.upcoming_deadlines.map((deadline) => (
              <Link
                key={deadline.id}
                to={`/course-instances/${deadline.id}`}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition"
              >
                <div>
                  <div className="font-medium text-gray-900">{deadline.title}</div>
                  {deadline.exam_title && (
                    <div className="text-sm text-gray-600">{deadline.exam_title}</div>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-amber-600">{formatDate(deadline.deadline)}</div>
                  <div className="text-xs text-gray-500">{deadline.candidates} kandidater</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Sensor Overview (for member/admin) */}
      {(user?.role === 'member' || user?.role === 'admin' || user?.role === 'systemadmin') && stats.sensors && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Users className="h-5 w-5 text-kf-primary" />
              Sensorstatus
            </h2>
            <Link to="/courses-and-exams?tab=sensors" className="text-sm text-kf-primary hover:underline flex items-center gap-1">
              Administrer <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-gray-900">{stats.sensors.active}</div>
              <div className="text-sm text-gray-600">Aktive sensorer</div>
            </div>
            <div className="text-center p-3 bg-amber-50 rounded-lg">
              <div className="text-2xl font-bold text-amber-600">{stats.sensors.pending_grading}</div>
              <div className="text-sm text-gray-600">Til sensur</div>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{stats.courses_exams.graded_total}</div>
              <div className="text-sm text-gray-600">Fullført totalt</div>
            </div>
          </div>
        </div>
      )}

      {/* System Stats (systemadmin only) */}
      {user?.role === 'systemadmin' && stats.system_stats && (
        <div className="card bg-slate-50">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Server className="h-5 w-5 text-slate-600" />
              Systemstatus
            </h2>
            <Link to="/settings" className="text-sm text-kf-primary hover:underline flex items-center gap-1">
              Innstillinger <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-3 bg-white rounded-lg">
              <div className="text-2xl font-bold text-gray-900">{stats.system_stats.total_associations}</div>
              <div className="text-sm text-gray-600">Studieforbund</div>
            </div>
            <div className="text-center p-3 bg-white rounded-lg">
              <div className="text-2xl font-bold text-gray-900">{stats.system_stats.total_users}</div>
              <div className="text-sm text-gray-600">Brukere totalt</div>
            </div>
            <div className="text-center p-3 bg-white rounded-lg">
              <div className="text-2xl font-bold text-green-600">{stats.system_stats.active_users}</div>
              <div className="text-sm text-gray-600">Aktive brukere</div>
            </div>
          </div>
        </div>
      )}

      {/* Recent Activity - NOT shown for sensors */}
      {user?.role !== 'sensor' && stats.recent_activity.length > 0 && (
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
            <Clock className="h-5 w-5 text-gray-500" />
            Siste aktivitet
          </h2>
          <div className="space-y-2">
            {stats.recent_activity.map((activity, index) => (
              <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                <div className="flex items-center gap-3">
                  {activity.type === 'study_plan' ? (
                    <FileText className="h-4 w-4 text-gray-400" />
                  ) : (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  )}
                  <span className="text-gray-900">{activity.title}</span>
                  <span className={`text-xs px-2 py-0.5 rounded ${getStatusColor(activity.status)}`}>
                    {formatStatus(activity.status)}
                  </span>
                </div>
                <span className="text-sm text-gray-500">{formatRelativeDate(activity.date)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions - Different for sensors */}
      {user?.role === 'sensor' ? (
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Hurtighandlinger</h2>
          <div className="flex flex-wrap gap-3">
            <Link to="/sensor/dashboard" className="btn btn-primary">
              Sensorportal
            </Link>
            {stats.courses_exams.pending_grading > 0 && (
              <Link to="/sensor/dashboard" className="btn btn-secondary">
                Se prøver til sensur ({stats.courses_exams.pending_grading})
              </Link>
            )}
          </div>
        </div>
      ) : (
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Hurtighandlinger</h2>
          <div className="flex flex-wrap gap-3">
            <Link to="/study-plans/new" className="btn btn-primary">
              + Ny studieplan
            </Link>
            {(user?.role === 'member' || user?.role === 'admin' || user?.role === 'systemadmin') && (
              <Link to="/courses-and-exams?tab=courses" className="btn btn-secondary">
                + Nytt kurs
              </Link>
            )}
            <Link to="/study-plans" className="btn btn-secondary">
              Alle studieplaner
            </Link>
            {(user?.role === 'admin' || user?.role === 'systemadmin') && (
              <Link to="/members" className="btn btn-secondary">
                Brukere
              </Link>
            )}
            {user?.role === 'systemadmin' && (
              <Link to="/settings" className="btn btn-secondary">
                Innstillinger
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Action Card Component
function ActionCard({ action }: { action: ActionItem }) {
  const priorityColors = {
    high: 'bg-red-50 border-red-200 text-red-800',
    medium: 'bg-amber-50 border-amber-200 text-amber-800',
    low: 'bg-blue-50 border-blue-200 text-blue-800'
  };

  // Type-specific icons
  const typeIcons: Record<string, React.ReactNode> = {
    user_approval: <UserPlus className="h-5 w-5 text-red-500" />,
    review: <FileText className="h-5 w-5 text-amber-500" />,
    grading: <GraduationCap className="h-5 w-5 text-red-500" />,
    draft: <Clock className="h-5 w-5 text-blue-500" />,
    rejected: <AlertTriangle className="h-5 w-5 text-red-500" />,
    info: <Bell className="h-5 w-5 text-blue-500" />
  };

  // Fallback to priority-based icons
  const priorityIcons = {
    high: <AlertTriangle className="h-5 w-5 text-red-500" />,
    medium: <Clock className="h-5 w-5 text-amber-500" />,
    low: <Bell className="h-5 w-5 text-blue-500" />
  };

  const icon = typeIcons[action.type] || priorityIcons[action.priority];

  return (
    <Link
      to={action.link}
      className={`flex items-center justify-between p-4 rounded-lg border transition hover:shadow-md ${priorityColors[action.priority]}`}
    >
      <div className="flex items-center gap-3">
        {icon}
        <div className="flex flex-col">
          <span className="font-medium">{action.title}</span>
          {action.count > 0 && (
            <span className="text-sm opacity-75">{action.count} {action.count === 1 ? 'ventende' : 'ventende'}</span>
          )}
        </div>
      </div>
      <ChevronRight className="h-5 w-5 opacity-50" />
    </Link>
  );
}

// Mini Stat Component
function MiniStat({ label, value, color }: { label: string; value: number; color?: 'green' | 'yellow' | 'red' }) {
  const colorClasses = {
    green: 'text-green-600',
    yellow: 'text-amber-600',
    red: 'text-red-600'
  };

  return (
    <div className="text-center p-2 bg-gray-50 rounded">
      <div className={`text-xl font-bold ${color ? colorClasses[color] : 'text-gray-900'}`}>
        {value}
      </div>
      <div className="text-xs text-gray-600 truncate">{label}</div>
    </div>
  );
}
